import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Heading, Body } from '@swyftx/aviary/atoms/Typography';
import { Utility, Numeric } from '@swyftx/aviary/atoms/Typography';
import { Tick, ArrowLineOnlyRight } from '@swyftx/aviary/icons/outlined';

import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

type AccessCashCardProps = {
  hasError: boolean;
};

const AccessCashCard: React.FC<AccessCashCardProps> = ({ hasError }) => {
  const { t } = useTranslation('loans');
  const { navigate } = useNavigateRoute();

  const scrollToCalculator = () => {
    const el = document.getElementById('loan-calculator');
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  const rowItem = (body: string): JSX.Element => (
    <div className='bt-8 flex flex-row space-x-[12px] pt-8'>
      <Tick className='text-color-text-info' />
      <Body size='medium'> {body} </Body>
    </div>
  );

  const rateItem = (type: 'interest' | 'comparison'): JSX.Element => (
    <div className='flex-1'>
      <Utility variant='overline' color='secondary' weight='bold'>
        {t(`AccessCashBanner.card.rates.${type}.label`)}
      </Utility>
      <div className='flex flex-col'>
        <Numeric size='xxlarge' weight='bold' color='primary'>
          {t(`AccessCashBanner.card.rates.${type}.rate`)}
        </Numeric>
        <Numeric size='large' weight='bold' color='primary'>
          {t(`AccessCashBanner.card.rates.${type}.tag`)}
        </Numeric>
      </div>
    </div>
  );

  return (
    <Card className='max-w-[370px] flex-1 justify-center space-y-[16px] rounded-12 p-24' border={false}>
      <FlexLayout className='flex flex-col space-y-[2px]'>
        <Heading size='h5' className='semi-bold' color='primary'>
          {t('AccessCashBanner.card.header.title')}
        </Heading>
        <Body size='small' color='info'>
          {t('AccessCashBanner.card.header.description')}
        </Body>
      </FlexLayout>

      <FlexLayout className='flex flex-row space-x-[16px]'>
        {rateItem('interest')}
        {rateItem('comparison')}
      </FlexLayout>

      <FlexLayout className='flex flex-col space-y-[4px]'>
        {rowItem(t('AccessCashBanner.card.listItem.1'))}
        {rowItem(t('AccessCashBanner.card.listItem.2'))}
        {rowItem(t('AccessCashBanner.card.listItem.3'))}
      </FlexLayout>

      <FlexLayout className='flex flex-col space-y-[12px]'>
        <Button
          size='lg'
          onClick={() => navigate(NavigationURLs.LoansApplication)}
          color='primary'
          trailingIcon={<ArrowLineOnlyRight />}
          disabled={hasError}
        >
          {t('AccessCashBanner.card.buttonGroup.1')}
        </Button>
        <Button size='lg' onClick={scrollToCalculator} variant='outlined'>
          {t('AccessCashBanner.card.buttonGroup.2')}
        </Button>
      </FlexLayout>
    </Card>
  );
};
AccessCashCard.displayName = 'AccessCashCard';

export { AccessCashCard };
